import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { CommunityFamiliesReportRow } from '../../../swagger'
import { communities } from '../../../api/swagger'
import { useLoadingIds } from '../../../hooks/useLoadingIds'
import { useMountEffect } from '../../../hooks/useMountEffect'
import LoadingProgress from '../../Elements/LoadingProgress'
import EmptyPage from '../../Elements/EmptyPage'
import { Box, IconButton, styled, Typography } from '@mui/material'
import { PDFDownloadLink, PDFViewer } from '@react-pdf/renderer'
import FamiliesReportPDF from './FamiliesReportPDF'
import { Page } from '../../Elements/PageMargins'
import TitleContext from '../../../TitleContext'
import useEndpoint from '../../../hooks/useEndpoint'
import { LoadingContext } from '../../Context/LoadingContext'
import { useCommunitiesContext } from '../../Context/CommunitiesContext'
import DynamicBreadcrumbs from '../../Elements/DynamicBreadcrumbs'
import PdfIcon from '@mui/icons-material/PictureAsPdf'
import ContainedButton, {
  ContainedButtonVariant,
} from '../../Buttons/ContainedButton'
import { CanAccess } from '../../Elements/Access'
import { useShowOnDesktop } from '../../../hooks/useShowOnDesktop'
import { dateToDashString } from '../../../utils/dateUtility'

const ButtonsWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',

  paddingBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    width: '100%',
  },
}))

export const csvHeaders =
  '"Parent First Name","Parent Last Name","Email","Phone","Student Name","Student Birthday","Program","Enrollment Status","Status Date","Enrolled Date"'

export const downloadedCSVName = 'families-report.csv'

const title = 'Communities'

export const formatBirthday = (month?: number, year?: number): string => {
  if (month && year) {
    return `${month}/${year}`
  }
  return year ? `${year}` : ''
}

/**
 * Guards against empty dates.
 */
export const formatReportDate = (date?: Date): string => {
  if (!date || isNaN(new Date(date).getTime())) {
    return ''
  }
  return dateToDashString(date)
}

export const FamiliesReportPDFPreview: React.FC = () => {
  const { communityKey } = useParams()
  const { t } = useTranslation()
  const {
    CommunityFamiliesTab: {
      fetchCommunityFamiliesReport: fetchCommunityFamiliesReportLoadingId,
    },
  } = useLoadingIds()
  const { loadingIds } = useContext(LoadingContext)
  const { useTitleEffect } = useContext(TitleContext)
  const { communityName } = useCommunitiesContext()
  const navigate = useNavigate()
  useTitleEffect(title)
  const isShowOnDesktop = useShowOnDesktop()
  //   states
  const [rows, setRows] = useState<CommunityFamiliesReportRow[]>([])
  const [downloadUrl, setDownloadUrl] = useState<string | undefined>()

  const userAgent = navigator.userAgent.toLowerCase()
  const isMobileBrowser =
    /iphone|ipad|ipod|android|blackberry|windows phone|ipad|tablet|playbook|silk/g.test(
      userAgent
    )

  const { triggerFetch: triggerFetchFamiliesReport } = useEndpoint({
    loadingId: fetchCommunityFamiliesReportLoadingId,
    async swaggerCall() {
      const fetchedReport = await communities.fetchCommunityFamiliesReport({
        communityKey: +(communityKey as unknown as number),
      })
      setRows(fetchedReport.data)
    },
  })

  useMountEffect(() => {
    triggerFetchFamiliesReport()
  })

  useEffect(() => {
    if (rows.length > 0) {
      const csvRows: string[] = [
        // Our initial CSV rows are just the headers
        csvHeaders,
        // Map each enrollment row to a CSV row
        ...rows.map((row: CommunityFamiliesReportRow) => {
          const fields = [
            row.parentFirstName ?? '',
            row.parentLastName ?? '',
            row.parentEmail ?? '',
            row.parentPhone ?? '',
            row.studentName ?? '',
            formatBirthday(row.studentBirthMonth, row.studentBirthYear),
            row.programType ?? '',
            row.enrollmentStatus ?? '',
            formatReportDate(row.enrollmentStatusDate),
            formatReportDate(row.enrolledDate),
          ]
          return fields
            .map((field) => `"${String(field).replace(/"/g, '""')}"`)
            .join(',')
        }),
      ]

      // Join all rows with line breaks to form the final CSV content
      const csvRowsWithBreaks = csvRows.join('\n')

      // Create a Blob with the CSV content and generate an object URL
      const blob = new Blob([csvRowsWithBreaks], {
        type: 'text/csv',
      })
      const url = URL.createObjectURL(blob)
      // Store the CSV URL for downloading
      setDownloadUrl(url)
    }
  }, [rows])

  if (loadingIds.has(fetchCommunityFamiliesReportLoadingId)) {
    return <LoadingProgress />
  }

  if (rows.length === 0) {
    return (
      <EmptyPage
        message={
          <Typography variant="subtitle1" component="p">
            {t(
              'FamiliesReportPDFPreview.PDF.Preview.ErrorMessage',
              'No families found. Unable to generate the PDF report. Please try again later.'
            )}
          </Typography>
        }
      />
    )
  }

  const breadcrumbs = [
    {
      label: t(
        'FamiliesReportPDFPreview.Breadcrumb.Communities',
        'Communities'
      ),
      onClick: () => navigate(`/communities`),
    },
    {
      label: communityName,
      onClick: () =>
        navigate(`/communities/community-details/${communityKey}/families`),
    },
    {
      label: t(
        'FamiliesReportPDFPreview.Breadcrumb.FamiliesReport',
        'Families Report'
      ),
    },
  ]

  return (
    <CanAccess I="view" on="Enrollment">
      <Page>
        <DynamicBreadcrumbs breadcrumbs={breadcrumbs} />

        <ButtonsWrapper>
          <ContainedButton
            css={{
              width: isShowOnDesktop ? '35vw' : '100%',
            }}
            variant={ContainedButtonVariant.DownloadFamiliesCsv}
            id="Download Families CSV"
            download={downloadedCSVName}
            href={downloadUrl}
            disabled={!downloadUrl}
          />
        </ButtonsWrapper>

        <Box display="flex" flexDirection={'column'} height={'70vh'}>
          <Box position="relative" width="100%" height="100%">
            <Box
              position="absolute"
              width="100%"
              height="100%"
              top={10}
              zIndex={2}
            >
              {isMobileBrowser && (
                <Box
                  justifyContent="center"
                  alignItems="center"
                  display="flex"
                  flexDirection="column"
                  textAlign="center"
                >
                  <Typography>
                    {t(
                      'Families.PDF.Preview.PreviewNotAvailableMessage',
                      'Preview not available on mobile devices, please download the PDF clicking the button below.'
                    )}
                  </Typography>
                  <PDFDownloadLink
                    document={
                      <FamiliesReportPDF
                        rows={rows}
                        communityName={communityName}
                      />
                    }
                    fileName="families.pdf"
                  >
                    <IconButton color="success">
                      <PdfIcon />
                      <Typography>
                        {t('Report.PDF.Preview.DownloadPDF', 'Download PDF')}
                      </Typography>
                    </IconButton>
                  </PDFDownloadLink>
                </Box>
              )}
              <PDFViewer style={{ flex: 1, width: '100%', height: '100%' }}>
                <FamiliesReportPDF rows={rows} communityName={communityName} />
              </PDFViewer>
            </Box>
          </Box>
        </Box>
      </Page>
    </CanAccess>
  )
}

export default FamiliesReportPDFPreview
